import { useRouteContext } from "@tanstack/react-router";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useState, useMemo, useEffect, useRef } from "react";
import { SpecialtiesSelect } from "@/components/Selects/SpecialtiesSelect";
import {
  Search,
  User,
  Loader2,
  AlertCircle,
  RefreshCw,
  SlidersHorizontal,
  FilterX,
  Eye,
} from "lucide-react";
import { useOrganization } from "@/hooks/use-organization";
import { useRolePermissions, Permission } from "@/hooks/use-role-permissions";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useTRPCClient } from "@/trpc";
import { Badge } from "@/components/ui/badge";
import { ProfileModal } from "@/features/profile/components/ProfileModal";
import { AddToOrganizationButton } from "./add-to-organization-button";
import { Label } from "@/components/ui/label";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

const PROVIDERS_SHARED_PAGE_SIZE = 100;

export function ProvidersSharedPage() {
  const trpcClient = useTRPCClient();
  const { selectedOrgBusinessUnit } = useOrganization();
  const organizationBusinessUnitId = selectedOrgBusinessUnit?.id || undefined;
  const { user } = useRouteContext({ from: "/_org" }) as {
    user: { id?: string; is_admin: boolean } | null | undefined;
  };
  const { hasPermission } = useRolePermissions(user);
  const canManageAffiliations = hasPermission(Permission.ManageAffiliations);
  const [viewingProvider, setViewingProvider] = useState<{
    id: string;
  } | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [selectedSpecialty, setSelectedSpecialty] = useState("");
  const loadMoreRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
    }, 300);

    return () => clearTimeout(timeout);
  }, [searchTerm]);

  // Direct shares are personal, not tied to a business unit — never pass an
  // organizationBusinessUnitId here even when the viewer has one selected.
  const providerListInput = useMemo(
    () => ({
      source: "direct-share" as const,
      searchTerm: debouncedSearchTerm || undefined,
      specialtyCode: selectedSpecialty || undefined,
    }),
    [debouncedSearchTerm, selectedSpecialty],
  );

  const {
    data: providersData,
    isLoading,
    isError,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ["profileListInfiniteSharedWithMe", providerListInput],
    queryFn: ({ pageParam = 0 }) =>
      trpcClient.profile.list.query({
        ...providerListInput,
        limit: PROVIDERS_SHARED_PAGE_SIZE,
        offset: pageParam * PROVIDERS_SHARED_PAGE_SIZE,
      }),
    getNextPageParam: (lastPage, allPages) =>
      Array.isArray(lastPage) && lastPage.length === PROVIDERS_SHARED_PAGE_SIZE
        ? allPages.length
        : undefined,
    initialPageParam: 0,
  });

  const providers = useMemo(
    () => providersData?.pages.flatMap((page) => page) ?? [],
    [providersData],
  );

  useEffect(() => {
    const node = loadMoreRef.current;
    if (!node) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin: "300px" },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const handleResetFilters = () => {
    setSearchTerm("");
    setSelectedSpecialty("");
  };

  const hasFilters = Boolean(searchTerm.trim() || selectedSpecialty);
  const activeFilterCount = [searchTerm.trim(), selectedSpecialty].filter(
    Boolean,
  ).length;

  const renderFilterControls = (variant: "desktop" | "mobile") => (
    <div
      className={
        variant === "desktop"
          ? "grid grid-cols-1 gap-3 lg:grid-cols-[minmax(220px,1fr)_minmax(180px,256px)]"
          : "space-y-4"
      }
    >
      <div className="grid min-w-0 gap-2">
        <Label htmlFor={`provider-shared-search-${variant}`}>Search</Label>
        <div className="relative">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <Search className="h-4 w-4 text-slate-400" />
          </div>
          <Input
            id={`provider-shared-search-${variant}`}
            placeholder="Provider name"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>
      <div className="grid min-w-0 gap-2">
        <Label htmlFor={`provider-shared-specialty-${variant}`}>
          Specialty
        </Label>
        <SpecialtiesSelect
          inputId={`provider-shared-specialty-${variant}`}
          value={selectedSpecialty}
          isMulti={false}
          onChange={(value) => {
            setSelectedSpecialty(typeof value === "string" ? value : "");
          }}
          placeholder="Any specialty"
        />
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-8 space-y-4 sm:space-y-6">
        <div className="space-y-2">
          <h1 className="text-3xl font-bold text-slate-900">Shared with Me</h1>
          <p className="text-slate-600 text-sm sm:text-base">
            Providers who have been shared directly with you.
          </p>
        </div>

        <div className="space-y-3">
          <div className="hidden lg:block">
            {renderFilterControls("desktop")}
          </div>

          <div className="lg:hidden">
            <Collapsible>
              <CollapsibleTrigger asChild>
                <Button variant="outline" className="w-full justify-between">
                  <span className="inline-flex items-center gap-2">
                    <SlidersHorizontal className="h-4 w-4" />
                    Filters
                  </span>
                  {activeFilterCount > 0 && (
                    <Badge className="bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-50">
                      {activeFilterCount}
                    </Badge>
                  )}
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-3 rounded-md border border-slate-200 bg-white p-3 shadow-sm">
                {renderFilterControls("mobile")}
                {hasFilters && (
                  <Button
                    variant="outline"
                    className="mt-5 w-full"
                    onClick={handleResetFilters}
                  >
                    <FilterX className="mr-2 h-4 w-4" />
                    Clear filters
                  </Button>
                )}
              </CollapsibleContent>
            </Collapsible>
          </div>

          {hasFilters && (
            <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
              <span className="inline-flex items-center gap-1 font-semibold uppercase tracking-wide text-slate-500">
                <FilterX className="h-3.5 w-3.5" />
                Active filters
              </span>
              {searchTerm && (
                <Badge
                  variant="outline"
                  className="border-blue-200 text-blue-700"
                >
                  Search: {searchTerm}
                </Badge>
              )}
              {selectedSpecialty && (
                <Badge
                  variant="outline"
                  className="border-blue-200 text-blue-700"
                >
                  Specialty: {selectedSpecialty}
                </Badge>
              )}
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-3 text-slate-500 hover:text-slate-700"
                onClick={handleResetFilters}
              >
                <FilterX className="mr-1 h-3.5 w-3.5" />
                Clear all
              </Button>
            </div>
          )}
        </div>

        {isLoading ? (
          <Card className="border-dashed">
            <CardContent className="p-6 sm:p-8 md:p-12 flex flex-col items-center justify-center text-center">
              <div className="bg-blue-100 p-3 rounded-full mb-4">
                <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold mb-2">
                Loading providers
              </h3>
              <p className="text-gray-600 mb-6 max-w-md text-sm sm:text-base">
                Please wait while we fetch providers shared with you.
              </p>
            </CardContent>
          </Card>
        ) : isError ? (
          <Card className="border-dashed border-2 border-red-300 bg-red-50">
            <CardContent className="p-6 sm:p-8 md:p-12 flex flex-col items-center justify-center text-center">
              <div className="bg-red-100 p-3 rounded-full mb-4">
                <AlertCircle className="h-8 w-8 text-red-600" />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold mb-2">
                Failed to load providers
              </h3>
              <p className="text-gray-600 mb-6 max-w-md text-sm sm:text-base">
                {error instanceof Error
                  ? error.message
                  : "An error occurred while loading providers. Please try again."}
              </p>
              <Button
                className="bg-blue-600 hover:bg-blue-700"
                onClick={() => refetch()}
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Retry
              </Button>
            </CardContent>
          </Card>
        ) : providers.length > 0 ? (
          <>
            {/* Card Layout (mobile + tablet) */}
            <div className="block lg:hidden space-y-4 min-w-0">
              {providers.map((provider) => {
                const isHidden = provider.isRevealed === false;
                const displayName = isHidden
                  ? `Provider #${provider.userId.slice(-4)}`
                  : `${provider.firstName ?? ""} ${provider.lastName ?? ""}`.trim();

                return (
                  <Card
                    key={provider.id}
                    className="rounded-2xl border border-slate-200/80 bg-white shadow-sm"
                  >
                    <CardContent className="space-y-4 p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                          <Avatar className="h-12 w-12 flex-shrink-0">
                            <AvatarImage src="" alt="Provider" />
                            <AvatarFallback className="bg-gray-200">
                              <User className="h-6 w-6 text-gray-500" />
                            </AvatarFallback>
                          </Avatar>
                          <div className="w-0 flex-1">
                            <Button
                              variant="link"
                              className="text-blue-600 hover:text-blue-700 font-semibold p-0 h-auto text-left justify-start text-lg max-w-full"
                              onClick={() => {
                                setViewingProvider({ id: provider.userId });
                              }}
                            >
                              <span className="truncate">{displayName}</span>
                            </Button>
                            {!isHidden && (
                              <p className="mt-2 text-sm text-slate-600">
                                <span className="font-semibold text-slate-900">
                                  Primary specialty:
                                </span>{" "}
                                {provider.specialties[0]?.name ||
                                  "Not provided"}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex-shrink-0 flex flex-col gap-1.5">
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex items-center gap-1 text-xs"
                            onClick={() => {
                              setViewingProvider({ id: provider.userId });
                            }}
                          >
                            <Eye className="h-3 w-3" />
                            <span>View Profile</span>
                          </Button>
                          <AddToOrganizationButton
                            provider={provider}
                            organizationBusinessUnitId={
                              organizationBusinessUnitId
                            }
                            canManageAffiliations={canManageAffiliations}
                            className="text-xs"
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Desktop Table Layout */}
            <Card className="hidden lg:block p-0 border border-slate-200/80 shadow-sm overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 text-slate-600 uppercase tracking-wide text-xs">
                    <TableHead className="w-12"></TableHead>
                    <TableHead className="min-w-[150px]">Name</TableHead>
                    <TableHead className="min-w-[120px]">Specialty</TableHead>
                    <TableHead className="w-[240px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {providers.map((provider) => {
                    const isHidden = provider.isRevealed === false;
                    const displayName = isHidden
                      ? `Provider #${provider.userId.slice(-4)}`
                      : `${provider.firstName ?? ""} ${provider.lastName ?? ""}`.trim();

                    return (
                      <TableRow key={provider.id} className="hover:bg-slate-50">
                        <TableCell className="py-3">
                          <Avatar className="h-10 w-10">
                            <AvatarImage src="" alt="Provider" />
                            <AvatarFallback className="bg-gray-200">
                              <User className="h-5 w-5 text-gray-500" />
                            </AvatarFallback>
                          </Avatar>
                        </TableCell>
                        <TableCell className="py-3">
                          <Button
                            variant="link"
                            className="text-blue-600 hover:text-blue-700 font-semibold p-0 h-auto text-left justify-start max-w-[200px] min-w-0"
                            onClick={() => {
                              setViewingProvider({ id: provider.userId });
                            }}
                          >
                            <span className="truncate">{displayName}</span>
                          </Button>
                        </TableCell>
                        <TableCell className="py-3">
                          <span className="text-sm">
                            {isHidden
                              ? "Hidden until revealed"
                              : provider.specialties[0]?.name || "No specialty"}
                          </span>
                        </TableCell>
                        <TableCell className="py-3 text-right">
                          <div className="flex flex-col items-stretch gap-1.5">
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex items-center gap-1"
                              onClick={() => {
                                setViewingProvider({ id: provider.userId });
                              }}
                            >
                              <Eye className="h-4 w-4" />
                              <span>View Profile</span>
                            </Button>
                            <AddToOrganizationButton
                              provider={provider}
                              organizationBusinessUnitId={
                                organizationBusinessUnitId
                              }
                              canManageAffiliations={canManageAffiliations}
                            />
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Card>
            <div
              ref={loadMoreRef}
              className="flex min-h-10 items-center justify-center py-4 text-sm text-slate-500"
            >
              {isFetchingNextPage ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading more providers
                </span>
              ) : hasNextPage ? (
                "Scroll to load more providers"
              ) : (
                "All matching providers loaded"
              )}
            </div>
          </>
        ) : (
          <Card className="border-dashed">
            <CardContent className="p-6 sm:p-8 md:p-12 flex flex-col items-center justify-center text-center">
              <div className="bg-blue-100 p-3 rounded-full mb-4">
                <User className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold mb-2">
                No providers found
              </h3>
              <p className="text-gray-600 mb-6 max-w-md text-sm sm:text-base">
                {hasFilters
                  ? "There are no providers matching your search criteria."
                  : "You don't have any providers shared with you yet."}
              </p>
              {hasFilters && (
                <Button
                  variant="outline"
                  onClick={handleResetFilters}
                  className="flex items-center gap-2"
                >
                  <FilterX size={16} />
                  Reset Filters
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </div>
      {viewingProvider && (
        <ProfileModal
          userId={viewingProvider.id}
          onClose={() => setViewingProvider(null)}
          organizationBusinessUnitId={organizationBusinessUnitId}
        />
      )}
    </div>
  );
}
